import { useContext, useEffect, useMemo, useRef } from "react";
import { alpha, Theme, useTheme } from "@material-ui/core";
import { GoogleMapContext } from "../context";
import "./GoogleMapLiveTrackingMarker.css";

interface Props {
  lat: number;
  lng: number;
  bearing?: number | null;
  moving?: boolean;
  vehicleName?: string;
}

interface MarkerAppearance {
  ambientColor: string;
  arrowColor: string;
  badgeColor: string;
  badgeHighlightColor: string;
  borderColor: string;
  glowColor: string;
  labelBackgroundColor: string;
  labelTextColor: string;
  ringColor: string;
  rippleColor: string;
  shadowColor: string;
}

interface OverlayMarkerProps extends Omit<Props, "moving"> {
  appearance: MarkerAppearance;
  isMoving?: boolean;
}

type LiveTrackingOverlay = google.maps.OverlayView & {
  update: (input: OverlayMarkerProps) => void;
};

const createLiveTrackingMarkerOverlay = () =>
  class LiveTrackingMarkerOverlay extends google.maps.OverlayView {
    private appearance: MarkerAppearance;
    private bearing: number;
    private isMoving: boolean;
    private position: google.maps.LatLng;
    private readonly root: HTMLDivElement;
    private readonly badge: HTMLDivElement;
    private readonly label: HTMLDivElement | null;

    constructor({
      appearance,
      bearing,
      isMoving,
      lat,
      lng,
      vehicleName,
    }: OverlayMarkerProps) {
      super();
      this.appearance = appearance;
      this.bearing = bearing ?? 0;
      this.isMoving = !!isMoving;
      this.position = new google.maps.LatLng(lat, lng);

      this.root = document.createElement("div");
      this.root.className = "live-tracking-marker-overlay";
      this.root.innerHTML = `
        <div class="live-tracking-marker">
          <div class="live-tracking-marker__label"></div>
          <span class="live-tracking-marker__halo"></span>
          <span class="live-tracking-marker__glow"></span>
          <span class="live-tracking-marker__glow live-tracking-marker__glow--secondary"></span>
          <span class="live-tracking-marker__ripple"></span>
          <span class="live-tracking-marker__ripple live-tracking-marker__ripple--delayed"></span>
          <div class="live-tracking-marker__badge">
            <span class="live-tracking-marker__core"></span>
            <svg class="live-tracking-marker__arrow" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"></path>
            </svg>
          </div>
        </div>
      `;
      this.badge = this.root.querySelector(
        ".live-tracking-marker__badge"
      ) as HTMLDivElement;
      this.label = this.root.querySelector(
        ".live-tracking-marker__label"
      ) as HTMLDivElement | null;

      this.applyAppearance(vehicleName);
    }

    onAdd() {
      this.getPanes()?.overlayMouseTarget.appendChild(this.root);
    }

    onRemove() {
      this.root.remove();
    }

    draw() {
      const projection = this.getProjection();
      if (!projection) {
        return;
      }

      const pixelPosition = projection.fromLatLngToDivPixel(this.position);
      if (!pixelPosition) {
        return;
      }

      this.root.style.transform = `translate(${pixelPosition.x}px, ${pixelPosition.y}px) translate(-50%, -50%)`;
    }

    update({
      appearance,
      bearing,
      isMoving,
      lat,
      lng,
      vehicleName,
    }: OverlayMarkerProps) {
      this.appearance = appearance;
      this.bearing = bearing ?? 0;
      this.isMoving = !!isMoving;
      this.position = new google.maps.LatLng(lat, lng);
      this.applyAppearance(vehicleName);
      this.draw();
    }

    private applyAppearance(vehicleName?: string) {
      this.root.style.setProperty(
        "--live-track-badge",
        this.appearance.badgeColor
      );
      this.root.style.setProperty(
        "--live-track-badge-highlight",
        this.appearance.badgeHighlightColor
      );
      this.root.style.setProperty(
        "--live-track-border",
        this.appearance.borderColor
      );
      this.root.style.setProperty(
        "--live-track-arrow",
        this.appearance.arrowColor
      );
      this.root.style.setProperty(
        "--live-track-ripple",
        this.appearance.rippleColor
      );
      this.root.style.setProperty(
        "--live-track-glow",
        this.appearance.glowColor
      );
      this.root.style.setProperty(
        "--live-track-ambient",
        this.appearance.ambientColor
      );
      this.root.style.setProperty(
        "--live-track-shadow",
        this.appearance.shadowColor
      );
      this.root.style.setProperty(
        "--live-track-ring",
        this.appearance.ringColor
      );
      this.root.style.setProperty(
        "--live-track-label-bg",
        this.appearance.labelBackgroundColor
      );
      this.root.style.setProperty(
        "--live-track-label-text",
        this.appearance.labelTextColor
      );
      this.root.classList.toggle("is-moving", this.isMoving);
      this.badge.style.transform = `rotate(${this.bearing}deg)`;
      if (this.label) {
        this.label.textContent = vehicleName ?? "";
        this.label.style.display = vehicleName ? "block" : "none";
      }
    }
  };

export const GoogleMapLiveTrackingMarker = ({
  lat,
  lng,
  bearing,
  moving,
  vehicleName,
}: Props) => {
  const { map } = useContext(GoogleMapContext);
  const theme = useTheme<Theme>();
  const overlayRef = useRef<LiveTrackingOverlay | null>(null);

  const appearance = useMemo(
    () => {
      const isDarkTheme = theme.palette.type === "dark";
      const primaryColor = isDarkTheme
        ? theme.palette.primary.light
        : theme.palette.primary.main;

      return {
        ambientColor: alpha(primaryColor, isDarkTheme ? 0.34 : 0.22),
        badgeColor: isDarkTheme
          ? alpha(theme.palette.background.paper, 0.92)
          : alpha(theme.palette.common.white, 0.98),
        badgeHighlightColor: alpha(
          isDarkTheme ? theme.palette.common.white : primaryColor,
          isDarkTheme ? 0.18 : 0.14
        ),
        borderColor: alpha(primaryColor, isDarkTheme ? 0.98 : 0.9),
        arrowColor: primaryColor,
        glowColor: alpha(primaryColor, isDarkTheme ? 0.5 : 0.38),
        labelBackgroundColor: isDarkTheme
          ? alpha("#0f172a", 0.9)
          : alpha(theme.palette.common.white, 0.92),
        labelTextColor: isDarkTheme
          ? alpha(theme.palette.common.white, 0.94)
          : "#111827",
        ringColor: alpha(
          isDarkTheme ? theme.palette.common.white : primaryColor,
          isDarkTheme ? 0.16 : 0.18
        ),
        rippleColor: alpha(primaryColor, isDarkTheme ? 0.28 : 0.24),
        shadowColor: isDarkTheme
          ? alpha(theme.palette.common.black, 0.55)
          : alpha(theme.palette.common.black, 0.2),
      };
    },
    [theme]
  );

  useEffect(() => {
    if (!map) {
      return;
    }

    if (!overlayRef.current) {
      const LiveTrackingMarkerOverlay = createLiveTrackingMarkerOverlay();
      overlayRef.current = new LiveTrackingMarkerOverlay({
        appearance,
        bearing,
        isMoving: moving,
        lat,
        lng,
        vehicleName,
      }) as LiveTrackingOverlay;
      overlayRef.current.setMap(map);
      return;
    }

    overlayRef.current.update({
      appearance,
      bearing,
      isMoving: moving,
      lat,
      lng,
      vehicleName,
    });
  }, [appearance, bearing, lat, lng, map, moving, vehicleName]);

  useEffect(() => {
    return () => {
      overlayRef.current?.setMap(null);
      overlayRef.current = null;
    };
  }, [map]);

  return null;
};
